import { useState } from 'react'

import { FxButton, FxIcon, FxTextField, FxUploadDialog } from '@/components/fx'
import {
  DEFAULT_MAX_UPLOAD_MB,
  UPLOAD_ACCEPT,
  UPLOAD_FORMATS_LABEL,
} from '@/components/upload/uploadQueue'
import type { SinglePage } from '@/types/domain'

import { editErrorMessage, type TemplateEdits } from './useTemplateEdits'
import { layoutName } from './templateTabModel'

/**
 * "Variante hinzufügen": a second version of the same form — an older
 * year's layout, a different print run — uploaded as an image or a PDF.
 *
 * Two things make it a dialog rather than a plain file picker: the variant
 * needs a name, and on a multi-page form it needs to say *which* page it is
 * an alternative to. Both are form fields the gateway reads
 * (`variantOf`/`label` on `POST /v1/templates/:id/pages`), so getting them
 * afterwards would mean a second request that can fail on its own.
 *
 * A PDF has no image when the request answers: the page comes back
 * `converting`, and the card it creates says so and watches until the
 * converter is done.
 */
export function AddVariantDialog({
  open,
  onClose,
  targets,
  edits,
}: {
  open: boolean
  onClose: () => void
  /** The primary pages a variant may attach to — `variantOf` is one level
   *  deep, so a variant is never a target. */
  targets: SinglePage[]
  edits: TemplateEdits
}) {
  const [file, setFile] = useState<File | null>(null)
  const [label, setLabel] = useState('')
  const [chosenTarget, setChosenTarget] = useState<number | undefined>()
  // Falls back to the first primary page rather than being seeded with it:
  // the dialog can mount before the template has loaded, and a seeded value
  // would then stay undefined for the whole session.
  const variantOf = chosenTarget ?? targets[0]?.page
  const { addPage } = edits

  if (!open) return null

  const close = () => {
    setFile(null)
    setLabel('')
    addPage.reset()
    onClose()
  }

  const submit = () => {
    if (!file) return
    addPage.mutate(
      { file, label: label.trim() || undefined, variantOf },
      { onSuccess: close },
    )
  }

  return (
    <FxUploadDialog
      title="Layout-Variante hinzufügen"
      subtitle={
        <>
          Laden Sie eine weitere Fassung <b>desselben Formulars</b> hoch. Die Variante teilt sich
          die Felder mit der Standard-Fassung — im Export bleibt es dieselbe Spalte.
        </>
      }
      accept={UPLOAD_ACCEPT}
      formats={UPLOAD_FORMATS_LABEL}
      maxSizeLabel={`Maximalgröße: ${DEFAULT_MAX_UPLOAD_MB} MB`}
      onFilesSelected={(files) => setFile(files[0] ?? null)}
      onClose={close}
      footer={
        <div className="flex flex-wrap justify-end gap-3">
          <FxButton variant="outlined" onClick={close}>
            Abbrechen
          </FxButton>
          <FxButton disabled={!file || addPage.isPending} onClick={submit}>
            {addPage.isPending ? 'Wird hochgeladen …' : 'Variante hinzufügen'}
          </FxButton>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {file && (
          <p className="flex items-center gap-2 text-body text-text">
            <FxIcon name="description" size={20} className="text-primary" />
            {file.name}
          </p>
        )}

        <FxTextField
          label="Bezeichnung"
          placeholder="z. B. 2018"
          hint="Der Name, unter dem diese Fassung in der Vorlage und in der Dokumentliste erscheint."
          value={label}
          onChange={(event) => setLabel(event.target.value)}
        />

        {/* Only worth asking on a form with more than one page: with a single
            page there is exactly one thing a variant can be an alternative
            to, and a select with one option is a question without a choice. */}
        {targets.length > 1 && (
          <div className="flex flex-col gap-1">
            <label htmlFor="variant-of" className="text-small text-text">
              Alternative zu
            </label>
            <select
              id="variant-of"
              className="h-control rounded-md bg-ground px-3 text-body text-text"
              value={variantOf ?? ''}
              onChange={(event) => setChosenTarget(Number(event.target.value))}
            >
              {targets.map((page) => (
                <option key={page.page} value={page.page}>
                  {layoutName(page)}
                </option>
              ))}
            </select>
          </div>
        )}

        {addPage.isError && (
          <p role="alert" className="text-body text-danger-text">
            {editErrorMessage(addPage.error)}
          </p>
        )}
      </div>
    </FxUploadDialog>
  )
}
